import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';
import type { SupabaseAuth } from '../auth/supabase';
import { api } from './client';
import { queryKeys } from './queries';
import type { ConnectedAgent } from './types';

/** Which step of the ordered revoke failed. */
export class RevokeError extends Error {
  constructor(
    readonly step: 'grant' | 'record',
    override readonly cause: unknown
  ) {
    super(
      step === 'grant'
        ? 'Could not revoke access at the sign-in service.'
        : 'Access was revoked, but recording it failed.'
    );
  }
}

export function useConnectedAgents(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.connectedAgents(),
    queryFn: ({ signal }) => api.listConnectedAgents(signal).then((r) => r.agents),
    enabled
  });
}

/**
 * Grant first, record second. A failed record keeps the grant marked deleted, so a retry only records.
 * The grant step is idempotent (an already-gone grant counts as revoked), so a lost marker only costs one extra request.
 */
export function useRevokeConnection(auth: SupabaseAuth | null) {
  const client = useQueryClient();
  const grantGone = useRef(new Set<string>());
  return useMutation({
    mutationFn: async (agent: Pick<ConnectedAgent, 'id' | 'clientId'>) => {
      if (!auth) throw new RevokeError('grant', new Error('Sign-in service unavailable'));
      if (!grantGone.current.has(agent.id)) {
        try {
          await auth.revokeGrant(agent.clientId);
        } catch (e) {
          throw new RevokeError('grant', e);
        }
        grantGone.current.add(agent.id);
      }
      try {
        await api.markConnectionRevoked(agent.id);
      } catch (e) {
        throw new RevokeError('record', e);
      }
      grantGone.current.delete(agent.id);
    },
    onSettled: () => client.invalidateQueries({ queryKey: queryKeys.connectedAgents() })
  });
}
